import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../api/axiosClient";

export type BookingStatus = "scheduled" | "in_progress" | "completed" | "cancelled" | "no_show";

export interface SaleBooking {
  booking_id: string;
  client_id: string;
  client_name: string;
  service: string;
  staff_id: string;
  start_time: string;
  end_time: string;
  status: BookingStatus;
  price: number;
}

interface SalesState {
  bookings: SaleBooking[];
  loading: boolean;
  error: string | null;
  updatingId: string | null;
}

const initialState: SalesState = {
  bookings: [],
  loading: false,
  error: null,
  updatingId: null,
};

function authHeader(): Record<string, string> {
  const token = window.localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface CreateBookingInput {
  client_id: string;
  staff_id: string;
  service: string;
  service_id?: string;
  start_time: string;
  end_time: string;
  price: number;
}

export const createBooking = createAsyncThunk(
  "sales/createBooking",
  async (input: CreateBookingInput, { rejectWithValue }) => {
    try {
      const res = await axiosClient.post("/bookings", input, {
        headers: authHeader(),
      });
      return res.data as { booking_id: string };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create booking");
    }
  }
);

export const fetchSalesBookings = createAsyncThunk(
  "sales/fetchBookings",
  async (
    { businessId, date }: { businessId: string; date: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await axiosClient.get("/sales/bookings", {
        params: { businessId, date },
        headers: authHeader(),
      });
      return res.data as SaleBooking[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch bookings");
    }
  }
);

export const updateBookingStatus = createAsyncThunk(
  "sales/updateStatus",
  async (
    { bookingId, status }: { bookingId: string; status: BookingStatus },
    { rejectWithValue }
  ) => {
    try {
      await axiosClient.patch(`/bookings/${bookingId}/status`, { status }, {
        headers: authHeader(),
      });
      return { bookingId, status };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update status");
    }
  }
);

export const rescheduleBooking = createAsyncThunk(
  "sales/reschedule",
  async (
    { bookingId, startTime, endTime }: { bookingId: string; startTime: string; endTime: string },
    { rejectWithValue }
  ) => {
    try {
      await axiosClient.patch(
        `/bookings/${bookingId}/reschedule`,
        { start_time: startTime, end_time: endTime },
        { headers: authHeader() }
      );
      return { bookingId, startTime, endTime };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to reschedule appointment");
    }
  }
);

const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload;
      })
      .addCase(fetchSalesBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateBookingStatus.pending, (state, action) => {
        state.updatingId = action.meta.arg.bookingId;
      })
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        state.updatingId = null;
        const { bookingId, status } = action.payload;
        const idx = state.bookings.findIndex((b) => b.booking_id === bookingId);
        if (idx !== -1) state.bookings[idx].status = status;
      })
      .addCase(updateBookingStatus.rejected, (state, action) => {
        state.updatingId = null;
        state.error = action.payload as string;
      })
      .addCase(rescheduleBooking.pending, (state, action) => {
        state.updatingId = action.meta.arg.bookingId;
      })
      .addCase(rescheduleBooking.fulfilled, (state, action) => {
        state.updatingId = null;
        const { bookingId, startTime, endTime } = action.payload;
        const idx = state.bookings.findIndex((b) => b.booking_id === bookingId);
        if (idx !== -1) {
          state.bookings[idx].start_time = startTime;
          state.bookings[idx].end_time = endTime;
        }
      })
      .addCase(rescheduleBooking.rejected, (state, action) => {
        state.updatingId = null;
        state.error = action.payload as string;
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default salesSlice.reducer;
